import React, { useState, useEffect } from "react";
import EmployeeNavbar from "../Layout/EmployeeNavbar";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  Table,
  Spinner,
  Badge,
  ButtonGroup,
} from "react-bootstrap";
import API_BASE_URL from "../config";

const EmployeeAssetRequest = () => {
  const navigate = useNavigate();

  const [collapsed, setCollapsed] = useState(false);
  const [activeSection, setActiveSection] = useState("asset-request");

  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approvalView, setApprovalView] = useState("timeline"); // "timeline" or "table"

  const [formData, setFormData] = useState({
    assetCategory: "",
    assetType: "",
    quantity: 1,
    justification: "",
    expectedDate: "",
    priority: "",
    attachments: [],
  });

  const [assignedAssets, setAssignedAssets] = useState([]);
  const [assetRequests, setAssetRequests] = useState([]);

  const toggleSidebar = () => setCollapsed((state) => !state);

  // Load Bootstrap CDN (if not globally imported)
  useEffect(() => {
    const bootstrapLink = document.createElement("link");
    bootstrapLink.rel = "stylesheet";
    bootstrapLink.href =
      "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";

    const iconsLink = document.createElement("link");
    iconsLink.rel = "stylesheet";
    iconsLink.href =
      "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";

    document.head.appendChild(bootstrapLink);
    document.head.appendChild(iconsLink);

    return () => {
      bootstrapLink.remove();
      iconsLink.remove();
    };
  }, []);

  // Fetch assigned assets
  useEffect(() => {
    if (!employee) return;

    const fetchAssignedAssets = async () => {
      try {
        const token = sessionStorage.getItem("token");
        const res = await fetch(
          `${API_BASE_URL}/api/assigned-assets/employee/${employee.id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!res.ok) throw new Error("Failed to fetch assigned assets");
        const data = await res.json();
        setAssignedAssets(data);
      } catch (err) {
        console.error(err);
        toast.error("Unable to fetch assigned assets");
      }
    };

    fetchAssignedAssets();
  }, [employee]);

  // Fetch asset requests
  useEffect(() => {
    if (!employee) return;

    const fetchAssetRequests = async () => {
      try {
        const token = sessionStorage.getItem("token");
        const res = await fetch(
          `${API_BASE_URL}/api/asset-requests/employee/${employee.id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!res.ok) throw new Error("Failed to fetch asset requests");
        const data = await res.json();
        setAssetRequests(data);
      } catch (err) {
        console.error(err);
      }
    };

    fetchAssetRequests();
  }, [employee]);

  // Fetch Employee Data
  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        const token = sessionStorage.getItem("token");
        const employeeId = sessionStorage.getItem("employeeId");

        if (!token || !employeeId) {
          toast.error("Session expired. Please login again.");
          navigate("/");
          return;
        }

        const res = await fetch(
          `${API_BASE_URL}/api/employees/${employeeId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!res.ok) throw new Error("Failed to fetch employee");

        const data = await res.json();
        setEmployee(data);
      } catch (err) {
        console.error(err);
        toast.error("Unable to fetch employee details");
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeData();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = sessionStorage.getItem("token");
      const employeeId = sessionStorage.getItem("employeeId");

      const payload = new FormData();
      payload.append("assetCategory", formData.assetCategory);
      payload.append("assetType", formData.assetType);
      payload.append("quantity", formData.quantity);
      payload.append("justification", formData.justification);
      payload.append("expectedDate", formData.expectedDate);
      payload.append("priority", formData.priority);

      formData.attachments.forEach((file) => {
        payload.append("attachments", file);
      });

      const res = await fetch(
        `${API_BASE_URL}/api/asset-requests/${employeeId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: payload,
        }
      );

      if (!res.ok) throw new Error("Failed to submit request");

      toast.success("Asset request submitted successfully!");
      setFormData({
        assetCategory: "",
        assetType: "",
        quantity: 1,
        justification: "",
        expectedDate: "",
        priority: "",
        attachments: [],
      });
    } catch (err) {
      console.error(err);
      toast.error("Unable to submit asset request");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const getStatusBadgeVariant = (status) => {
    if (!status) return "secondary";
    const s = status.toLowerCase();
    if (s.includes("approved")) return "success";
    if (s.includes("rejected") || s.includes("declined")) return "danger";
    if (s.includes("pending")) return "warning";
    return "secondary";
  };

  const totalAssigned = assignedAssets.length;
  const totalRequests = assetRequests.length;
  const pendingRequests = assetRequests.filter((r) =>
    (r.status || "").toLowerCase().includes("pending")
  ).length;

  // NAVBAR height – adjust if your EmployeeNavbar is taller/shorter
  const NAVBAR_HEIGHT = 64;

  return (
    <>
      {/* Internal CSS for fixed navbar + scrollable content */}
      <style>
        {`
          .app-navbar-fixed {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            z-index: 1030; /* above cards/content */
          }

          .app-content-with-navbar {
            padding-top: ${NAVBAR_HEIGHT}px;
          }
        `}
      </style>

      <div className="bg-light min-vh-100">
        {/* Fixed navbar wrapper */}
        <div className="app-navbar-fixed">
          <EmployeeNavbar />
        </div>

        {/* All other content scrolls, offset by navbar height */}
        <div className="app-content-with-navbar" style={{ paddingTop: "120px" }}>
          <Container fluid className="px-3 px-md-4 pb-4">
            {loading ? (
              <div className="d-flex flex-column align-items-center justify-content-center py-5">
                <Spinner animation="border" role="status" />
                <p className="mt-3 text-muted mb-0">
                  Loading employee details...
                </p>
              </div>
            ) : (
              <Row className="g-3">
                {/* SIDEBAR */}
                <Col
                  xs={collapsed ? 2 : 12}
                  sm={collapsed ? 2 : 4}
                  md={collapsed ? 2 : 3}
                  lg={collapsed ? 1 : 2}
                  xl={collapsed ? 1 : 2}
                >
                  <Card className="shadow-sm border-0 h-100">
                    <Card.Body className="d-flex flex-column p-3">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        {!collapsed && (
                          <div>
                            <h6 className="mb-0 fw-semibold">Asset Portal</h6>
                            <small className="text-muted">
                              Employee self-service
                            </small>
                          </div>
                        )}
                        <Button
                          variant="light"
                          size="sm"
                          className="border rounded-circle d-flex align-items-center justify-content-center"
                          onClick={toggleSidebar}
                        >
                          <i
                            className={`bi ${
                              collapsed
                                ? "bi-arrow-right-square"
                                : "bi-arrow-left-square"
                            }`}
                          />
                        </Button>
                      </div>

                      <div className="d-flex flex-column gap-2 mt-2">
                        <Button
                          variant={
                            activeSection === "asset-request"
                              ? "primary"
                              : "outline-primary"
                          }
                          className="d-flex align-items-center justify-content-start"
                          onClick={() => setActiveSection("asset-request")}
                        >
                          <i className="bi bi-archive me-2" />
                          {!collapsed && <span>Asset Request</span>}
                        </Button>

                        <Button
                          variant={
                            activeSection === "assigned-assets"
                              ? "primary"
                              : "outline-primary"
                          }
                          className="d-flex align-items-center justify-content-start"
                          onClick={() => setActiveSection("assigned-assets")}
                        >
                          <i className="bi bi-list-check me-2" />
                          {!collapsed && <span>Assigned Assets</span>}
                        </Button>

                        <Button
                          variant={
                            activeSection === "asset-approval"
                              ? "primary"
                              : "outline-primary"
                          }
                          className="d-flex align-items-center justify-content-start"
                          onClick={() => setActiveSection("asset-approval")}
                        >
                          <i className="bi bi-check-circle me-2" />
                          {!collapsed && <span>Asset Approval</span>}
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                {/* MAIN CONTENT */}
                <Col
                  xs={12}
                  sm={collapsed ? 10 : 8}
                  md={collapsed ? 10 : 9}
                  lg={collapsed ? 11 : 10}
                  xl={collapsed ? 11 : 10}
                >
                  {/* Header */}
                  <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-3">
                    <div>
                      <h4 className="mb-0">
                        {activeSection === "asset-request" &&
                          "New Asset Request"}
                        {activeSection === "assigned-assets" &&
                          "Assigned Assets"}
                        {activeSection === "asset-approval" &&
                          "Asset Approval Status"}
                      </h4>
                      {employee && (
                        <small className="text-muted">
                          {employee.firstName} {employee.lastName} •{" "}
                          {employee.designation}
                        </small>
                      )}
                    </div>

                    {/* Metrics summary */}
                    <div className="d-flex gap-2 mt-2 mt-md-0">
                      <Badge bg="light" text="dark" className="border">
                        <i className="bi bi-laptop me-1" />
                        Assigned: {totalAssigned}
                      </Badge>
                      <Badge bg="light" text="dark" className="border">
                        <i className="bi bi-file-earmark-text me-1" />
                        Requests: {totalRequests}
                      </Badge>
                      <Badge bg="light" text="dark" className="border">
                        <i className="bi bi-hourglass-split me-1" />
                        Pending: {pendingRequests}
                      </Badge>
                    </div>
                  </div>

                  {/* ASSET REQUEST */}
                  {activeSection === "asset-request" && (
                    <Card className="shadow-sm border-0">
                      <Card.Body className="p-4">
                        {/* Employee details */}
                        <Row className="g-4 mb-3">
                          <Col lg={12}>
                            <Card className="border-0 bg-white">
                              <Card.Body>
                                <Row>
                                  <Col md={6} className="mb-3 mb-md-0">
                                    <p className="text-uppercase text-muted small mb-1">
                                      Employee Name
                                    </p>
                                    <h6 className="fw-semibold mb-2">
                                      {employee.firstName} {employee.lastName}
                                    </h6>

                                    <p className="text-uppercase text-muted small mb-1">
                                      Department
                                    </p>
                                    <div className="fw-semibold mb-2">
                                      {employee.department}
                                    </div>

                                    <p className="text-uppercase text-muted small mb-1">
                                      Manager
                                    </p>
                                    <div className="fw-semibold mb-2">
                                      {employee.reporter
                                        ? `${employee.reporter.firstName} ${employee.reporter.lastName}`
                                        : "N/A"}
                                    </div>

                                    <p className="text-uppercase text-muted small mb-1">
                                      Joining Date
                                    </p>
                                    <div className="fw-semibold">
                                      {employee.doj || "N/A"}
                                    </div>
                                  </Col>

                                  <Col md={6}>
                                    <p className="text-uppercase text-muted small mb-1">
                                      Employee ID
                                    </p>
                                    <h6 className="fw-semibold mb-2">
                                      {employee.id}
                                    </h6>

                                    <p className="text-uppercase text-muted small mb-1">
                                      Location
                                    </p>
                                    <div className="fw-semibold mb-2">
                                      {employee.city}, {employee.state}
                                    </div>

                                    <p className="text-uppercase text-muted small mb-1">
                                      Job Role
                                    </p>
                                    <div className="fw-semibold mb-2">
                                      {employee.designation}
                                    </div>

                                    <p className="text-uppercase text-muted small mb-1">
                                      Contact
                                    </p>
                                    <div className="fw-semibold">
                                      {employee.phone ||
                                        employee.contact ||
                                        "N/A"}
                                    </div>
                                  </Col>
                                </Row>
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>

                        <hr className="my-3" />

                        {/* Request Form */}
                        <Form onSubmit={handleSubmit}>
                          <Row className="g-3">
                            <Col md={4}>
                              <Form.Group>
                                <Form.Label>Asset Category</Form.Label>
                                <Form.Select
                                  name="assetCategory"
                                  value={formData.assetCategory}
                                  onChange={handleChange}
                                >
                                  <option value="">Select Category</option>
                                  <option>Laptop</option>
                                  <option>Monitor</option>
                                  <option>Keyboard</option>
                                  <option>Mouse</option>
                                </Form.Select>
                              </Form.Group>
                            </Col>

                            <Col md={4}>
                              <Form.Group>
                                <Form.Label>Asset Type / Model</Form.Label>
                                <Form.Control
                                  name="assetType"
                                  value={formData.assetType}
                                  onChange={handleChange}
                                  placeholder="e.g. MacBook Pro 15”"
                                />
                              </Form.Group>
                            </Col>

                            <Col md={4}>
                              <Form.Group>
                                <Form.Label>Quantity</Form.Label>
                                <Form.Control
                                  type="number"
                                  min="1"
                                  name="quantity"
                                  value={formData.quantity}
                                  onChange={handleChange}
                                />
                              </Form.Group>
                            </Col>

                            <Col md={4}>
                              <Form.Group>
                                <Form.Label>Expected Date</Form.Label>
                                <Form.Control
                                  type="date"
                                  name="expectedDate"
                                  value={formData.expectedDate}
                                  onChange={handleChange}
                                />
                              </Form.Group>
                            </Col>

                            <Col md={4}>
                              <Form.Group>
                                <Form.Label>Priority</Form.Label>
                                <Form.Select
                                  name="priority"
                                  value={formData.priority}
                                  onChange={handleChange}
                                >
                                  <option value="">Select Priority</option>
                                  <option value="Low">Low</option>
                                  <option value="Medium">Medium</option>
                                  <option value="High">High</option>
                                </Form.Select>
                              </Form.Group>
                            </Col>

                            <Col md={6}>
                              <Form.Group>
                                <Form.Label>Justification</Form.Label>
                                <Form.Control
                                  as="textarea"
                                  rows={3}
                                  name="justification"
                                  value={formData.justification}
                                  onChange={handleChange}
                                  placeholder="Provide a clear justification for this request"
                                />
                              </Form.Group>
                            </Col>

                            <Col md={6}>
                              <Form.Group>
                                <Form.Label>Attachments</Form.Label>
                                <Form.Control
                                  type="file"
                                  name="attachments"
                                  multiple
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      attachments: Array.from(e.target.files),
                                    }))
                                  }
                                />
                                <Form.Text className="text-muted">
                                  Upload relevant documents, images, or PDFs.
                                </Form.Text>
                              </Form.Group>
                            </Col>

                            <Col
                              md={12}
                              className="d-flex gap-2 justify-content-end mt-3"
                            >
                              <Button
                                variant="outline-secondary"
                                type="button"
                                onClick={() =>
                                  setFormData({
                                    assetCategory: "",
                                    assetType: "",
                                    quantity: 1,
                                    justification: "",
                                    expectedDate: "",
                                    priority: "",
                                    attachments: [],
                                  })
                                }
                              >
                                Clear
                              </Button>
                              <Button variant="outline-primary" type="button">
                                Save as Draft
                              </Button>
                              <Button type="submit" variant="primary">
                                Submit Request
                              </Button>
                            </Col>
                          </Row>
                        </Form>
                      </Card.Body>
                    </Card>
                  )}

                  {/* ASSIGNED ASSETS */}
                  {activeSection === "assigned-assets" && (
                    <Card className="p-3 p-md-4 shadow-sm border-0">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">Your Assigned Assets</h5>
                      </div>
                      <Table
                        bordered
                        hover
                        responsive
                        className="align-middle mb-0"
                      >
                        <thead className="table-light">
                          <tr>
                            <th>Asset</th>
                            <th>Model</th>
                            <th>Assigned On</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {assignedAssets.length === 0 ? (
                            <tr>
                              <td colSpan={4} className="text-center text-muted">
                                No assets assigned yet.
                              </td>
                            </tr>
                          ) : (
                            assignedAssets.map((a) => (
                              <tr key={a.id}>
                                <td>{a.asset}</td>
                                <td>{a.model}</td>
                                <td>{a.assignedOn}</td>
                                <td>
                                  <Badge
                                    bg={getStatusBadgeVariant(a.status)}
                                    pill
                                  >
                                    {a.status}
                                  </Badge>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </Card>
                  )}

                  {/* ASSET APPROVAL */}
                  {activeSection === "asset-approval" && (
                    <Card className="p-3 p-md-4 shadow-sm border-0">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                          <h5 className="mb-0">Asset Approval Status</h5>
                          <small className="text-muted">
                            Track the progress of your requests
                          </small>
                        </div>
                        <ButtonGroup size="sm">
                          <Button
                            variant={
                              approvalView === "timeline"
                                ? "primary"
                                : "outline-primary"
                            }
                            onClick={() => setApprovalView("timeline")}
                          >
                            <i className="bi bi-clock-history me-1" />
                            Timeline
                          </Button>
                          <Button
                            variant={
                              approvalView === "table"
                                ? "primary"
                                : "outline-primary"
                            }
                            onClick={() => setApprovalView("table")}
                          >
                            <i className="bi bi-table me-1" />
                            Table
                          </Button>
                        </ButtonGroup>
                      </div>

                      {approvalView === "table" ? (
                        <Table
                          bordered
                          hover
                          responsive
                          className="align-middle mb-0"
                        >
                          <thead className="table-light">
                            <tr>
                              <th>Asset</th>
                              <th>Requested On</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {assetRequests.length === 0 ? (
                              <tr>
                                <td
                                  colSpan={3}
                                  className="text-center text-muted"
                                >
                                  No asset requests found.
                                </td>
                              </tr>
                            ) : (
                              assetRequests.map((req) => (
                                <tr key={req.id}>
                                  <td>{req.assetType}</td>
                                  <td>{req.expectedDate}</td>
                                  <td>
                                    <Badge
                                      bg={getStatusBadgeVariant(req.status)}
                                      pill
                                    >
                                      {req.status}
                                    </Badge>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      ) : (
                        <div className="mt-2">
                          {assetRequests.length === 0 ? (
                            <p className="text-muted mb-0">
                              No asset requests found.
                            </p>
                          ) : (
                            <ul className="list-group list-group-flush">
                              {assetRequests.map((req) => (
                                <li
                                  className="list-group-item d-flex justify-content-between align-items-start"
                                  key={req.id}
                                >
                                  <div className="ms-0">
                                    <div className="fw-semibold">
                                      {req.assetType}
                                    </div>
                                    <small className="text-muted">
                                      Requested on: {req.expectedDate}
                                    </small>
                                  </div>
                                  <Badge
                                    bg={getStatusBadgeVariant(req.status)}
                                    pill
                                  >
                                    {req.status}
                                  </Badge>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </Card>
                  )}
                </Col>
              </Row>
            )}
          </Container>
        </div>
      </div>
    </>
  );
};

export default EmployeeAssetRequest;
